import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import FormFieldTsoPq, { typeProps } from '@components/Form/FormFieldTsoPq';
import FormTsoPq from '@components/Form/FormTsoPq';
import { history } from '@constants/measurment';
import {
  useDeleteTransducerModelByIdMutation,
  usePostTransducerModelMutation,
  usePutTransducerModelMutation,
} from '@redux/apiSlices/transducerModelApi';
import { useAppDispatch } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import {
  baseOption,
  commentSchema,
  mandatory,
  mandatoryStringSchema,
  stringPositiveIntegerSchema,
  transformerRatioSchema,
} from '@utils/validationSchema';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z, { object } from 'zod';
import FormModalWrapper from '../GenericModalWrapper';
import { StyledInputFieldWrapper, StyledInputsWrapper } from '../StyledMasterdataModal';
import { RowDataTransducer } from './Transducer';

type TransducerProps = {
  newTransducerModelType?: boolean;
  handleDiscard?: any;
  defaultValues?: Record<string, any>;
  selectedTransducer?: RowDataTransducer;
  customMaxWidth: string;
  updateDisabled?: boolean;
  deleteDisabled?: boolean;
  type?: any;
};

const FormContent = ({
  register,
  watch,
  setValue,
  errors,
  updateDisabled,
  defaultValues,
  onTypeChange,
}: {
  register: any;
  watch: any;
  setValue: any;
  errors: any;
  updateDisabled?: boolean;
  defaultValues?: Record<string, any>;
  onTypeChange?: any;
}) => {
  const { t } = useTranslation();
  let values = watch();

  useEffect(() => {
    onTypeChange(values.type);
  }, [values.type, onTypeChange]);

  const createDevitationFields = (
    prefix: 'voltage' | 'current',
    valuesSet: number[]
  ): Array<{
    name: string;
    label: string;
    required?: boolean;
    multiline?: boolean;
    rows?: number;
    customWidth?: string;
    type?: typeProps;
    options?: { id: number; label: string; value: number }[];
    fullWidth?: boolean;
    supportingText?: string;
    disabled?: boolean;
    visibleIf?: { field: string; value: number[] };
    group?: number;
  }> => [
    {
      name: `${prefix}FrequencyDeviationMagnitude1`,
      visibleIf: { field: 'type', value: valuesSet },
      type: 'integer',
      group: 2,
      customWidth: '17.75rem',
      label:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t('masterdata.transducer.table.frequencyDeviationMagnitude1'),
    },
    {
      name: `${prefix}FrequencyDeviationMagnitude10`,
      visibleIf: { field: 'type', value: valuesSet },
      type: 'integer',
      group: 2,
      customWidth: '17.75rem',
      label:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t('masterdata.transducer.table.frequencyDeviationMagnitude10'),
    },
    {
      name: `${prefix}FrequencyDeviationPhaseAngle1`,
      visibleIf: { field: 'type', value: valuesSet },
      type: 'integer',
      group: 2,
      customWidth: '17.75rem',
      label:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t('masterdata.transducer.table.frequencyDeviationPhaseAngle1'),
    },
    {
      name: `${prefix}FrequencyDeviationPhaseAngle10`,
      visibleIf: { field: 'type', value: valuesSet },
      type: 'integer',
      group: 2,
      customWidth: '17.75rem',
      label:
        t(`masterdata.transducer.modal.type.${prefix}`) +
        ' ' +
        t('masterdata.transducer.table.frequencyDeviationPhaseAngle10'),
    },
  ];

  const interleaveDevitationFields = (voltageArray: string | any[], currentArray: string | any[]) => {
    const interleavedArray = [];
    const maxLength = Math.max(voltageArray.length, currentArray.length);

    for (let i = 0; i < maxLength; i++) {
      if (i < voltageArray.length) interleavedArray.push(voltageArray[i]);
      if (i < currentArray.length) interleavedArray.push(currentArray[i]);
    }
    return interleavedArray;
  };

  const voltageFields = createDevitationFields('voltage', [0, 2]);
  const currentFields = createDevitationFields('current', [1, 2]);

  const fields: Array<{
    name: string;
    label: string;
    required?: boolean;
    multiline?: boolean;
    rows?: number;
    customWidth?: string;
    type?: typeProps;
    options?: { id: number; label: string; value: number }[];
    fullWidth?: boolean;
    supportingText?: string;
    disabled?: boolean;
    visibleIf?: { field: string; value: number[] };
    group?: number;
  }> = [
    {
      name: 'manufacturer',
      group: 1,
      label: t('masterdata.transducer.table.manufacturer'),
      required: true,
    },
    {
      name: 'model',
      group: 1,
      label: t('masterdata.transducer.table.model'),
      required: true,
    },
    {
      name: 'type',
      label: t('masterdata.transducer.table.transducerModel'),
      type: 'singleSelect',
      fullWidth: true,
      group: 1,
      options: [
        { id: 0, label: t('masterdata.transducer.modal.type.voltage'), value: 0 },
        { id: 1, label: t('masterdata.transducer.modal.type.current'), value: 1 },
        { id: 2, label: t('masterdata.transducer.modal.type.combined'), value: 2 },
      ],
      required: true,
    },
    {
      visibleIf: { field: 'type', value: [0, 2] },
      name: 'transformerRatioVoltage',
      type: 'text',
      label: t('masterdata.transducer.table.transmissionRatioVoltage'),
      fullWidth: true,
      group: 2,
      required: values.type === 2 || values.type === 0,
      customWidth: '17.75rem',
      disabled: updateDisabled,
      supportingText: updateDisabled
        ? t('masterdata.generalLimitationHasData')
        : t('masterdata.transducer.table.transmissionRatioFormat'),
    },
    {
      visibleIf: { field: 'type', value: [1, 2] },
      group: 2,
      fullWidth: true,
      required: values.type === 1 || values.type === 2,
      customWidth: '17.75rem',
      name: 'transformerRatioCurrent',
      type: 'text',
      label: t('masterdata.transducer.table.transmissionRatioCurrent'),
      supportingText: updateDisabled
        ? t('masterdata.generalLimitationHasData')
        : t('masterdata.transducer.table.transmissionRatioFormat'),
      disabled: updateDisabled,
    },
    ...interleaveDevitationFields(voltageFields, currentFields),
    {
      name: 'comment',
      label: t('management.substation.comment'),
      multiline: true,
      rows: 4,
      group: 3,
      customWidth: '40rem',
      fullWidth: true,
    },
  ];

  const isFieldVisible = (field: any, values: Record<string, any>) => {
    if (!field.visibleIf) return true;
    const { field: dependentField, value } = field.visibleIf;
    const current = values[dependentField];
    return Array.isArray(value) ? value.includes(current) : current === value;
  };

  const visibleFields = fields.filter((field) => isFieldVisible(field, values));
  const groupedFields = visibleFields.reduce(
    (visibleValue, field) => {
      const groupKey = String(field.group ?? 'default');
      if (!visibleValue[groupKey]) visibleValue[groupKey] = [];
      visibleValue[groupKey].push(field);
      return visibleValue;
    },
    {} as Record<string, typeof fields>
  );

  const StyledInputsWrapperStyle = (groupName: string) => {
    if (groupName === '2') {
      return {
        backgroundColor: tsoPqTheme.palette.background.neutral.color_neutral_background_bg_neutral_lt,
        padding: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
        marginTop: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24,
      };
    }
    if (groupName === '3') {
      return {
        padding: `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_24} 0 0 0`,
      };
    }
    return {
      backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
    };
  };

  const renderFields = Object.entries(groupedFields).map(([groupName, groupFields]) => (
    <StyledInputsWrapper
      key={groupName}
      fieldWidth={groupName == '2' ? '17.75rem' : '19.25rem'}
      style={StyledInputsWrapperStyle(groupName)}
    >
      {groupFields.map(
        ({
          name,
          label,
          required,
          multiline = false,
          rows = 1,
          customWidth,
          type = 'text',
          options,
          visibleIf,
          fullWidth,
          supportingText,
          disabled,
        }) => {
          const currentOptionsValue = visibleIf ? values[visibleIf.field] : undefined;
          const dynamicFullWidth = currentOptionsValue === 2 ? false : fullWidth;

          return (
            <StyledInputFieldWrapper key={name} $fullWidth={dynamicFullWidth}>
              <FormFieldTsoPq
                key={name}
                name={name}
                label={label}
                type={type}
                options={options}
                errorMessage="general.error.characterLimit"
                register={register}
                watch={watch}
                setValue={setValue}
                required={required}
                multiline={multiline}
                rows={rows}
                customWidth={customWidth}
                placeholder="masterdata.generalPlaceholder"
                errors={errors}
                supportingText={supportingText}
                disabled={disabled}
              />
            </StyledInputFieldWrapper>
          );
        }
      )}
    </StyledInputsWrapper>
  ));
  return renderFields;
};

const TransducerModal = ({
  newTransducerModelType,
  handleDiscard,
  defaultValues = {},
  selectedTransducer,
  customMaxWidth,
  updateDisabled,
  deleteDisabled,
}: TransducerProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [addEntity] = usePostTransducerModelMutation();
  const [editEntity] = usePutTransducerModelMutation();
  const [deleteTransducerModelById] = useDeleteTransducerModelByIdMutation();

  const [currentType, setCurrentType] = useState<string | undefined>('');
  useEffect(() => {}, [currentType]);

  const handleTypeChange = (newType: string) => {
    setCurrentType(newType);
  };

  function editingValues(selectedRatio: RowDataTransducer | string | null | undefined, selectedType: boolean) {
    return selectedRatio != null && selectedType != null;
  }

  function changeFromCombined(data_type: boolean, selectedType: boolean) {
    return data_type && selectedType;
  }

  function setValuesNull(
    data_transformerRatio: string | null | undefined,
    ratio: null | undefined,
    sel_transformerRatio: string | null | undefined
  ) {
    return {
      data_transformerRatio: data_transformerRatio ? null : undefined,
      ratio: ratio ? null : undefined,
      sel_transformerRatio: sel_transformerRatio ? null : undefined,
    };
  }

  function safeSplitRatio(data_transformerRatio: string | null | undefined) {
    return data_transformerRatio && data_transformerRatio.length > 0 ? data_transformerRatio.split(':') : undefined;
  }

  const processData = (data: any) => {
    let currentRatio;
    let voltageRatio;

    // editing values
    if (selectedTransducer?.type !== data.type && data.type != 2) {
      // change from current to voltage
      if (editingValues(selectedTransducer?.transformerRatioCurrent, selectedTransducer?.type == 1)) {
        setValuesNull(data.transformerRatioCurrent, currentRatio, selectedTransducer?.transformerRatioCurrent);
      }
      // change from voltage to current
      else if (editingValues(selectedTransducer?.transformerRatioVoltage, selectedTransducer?.type == 0)) {
        setValuesNull(data.transformerRatioVoltage, voltageRatio, selectedTransducer?.transformerRatioVoltage);
      }
      // change from combined to current
      else if (changeFromCombined(data?.type === 1, selectedTransducer?.type === 2)) {
        setValuesNull(data.transformerRatioVoltage, voltageRatio, selectedTransducer?.transformerRatioVoltage);
        setVoltageFrequenciesNull();
      }
      // change from combined to voltage
      else if (changeFromCombined(data?.type === 0, selectedTransducer?.type === 2)) {
        setValuesNull(data.transformerRatioCurrent, currentRatio, selectedTransducer?.transformerRatioCurrent);
        setCurrentFrequenciesNull();
      }
    }
    // adding new model
    if (currentType == '0') {
      voltageRatio = safeSplitRatio(data?.transformerRatioVoltage);
      currentRatio = null;
      setCurrentFrequenciesNull();
    }
    if (currentType == '1') {
      currentRatio = safeSplitRatio(data?.transformerRatioCurrent);
      setVoltageFrequenciesNull();
      voltageRatio = null;
    } else if (currentType == '2') {
      voltageRatio = safeSplitRatio(data?.transformerRatioVoltage);
      currentRatio = safeSplitRatio(data?.transformerRatioCurrent);
    }

    let { ...transducer } = data;

    function setVoltageFrequenciesNull() {
      data.voltageFrequencyDeviationMagnitude1 = undefined;
      data.voltageFrequencyDeviationMagnitude10 = undefined;
      data.voltageFrequencyDeviationPhaseAngle1 = undefined;
      data.voltageFrequencyDeviationPhaseAngle10 = undefined;
    }

    function setCurrentFrequenciesNull() {
      data.currentFrequencyDeviationMagnitude1 = undefined;
      data.currentFrequencyDeviationMagnitude10 = undefined;
      data.currentFrequencyDeviationPhaseAngle1 = undefined;
      data.currentFrequencyDeviationPhaseAngle10 = undefined;
    }

    const processedData = {
      ...transducer,
      voltageRatioPrimary: voltageRatio ? voltageRatio[0] : null,
      voltageRatioSecondary: voltageRatio ? voltageRatio[1] : null,
      currentRatioPrimary: currentRatio ? currentRatio[0] : null,
      currentRatioSecondary: currentRatio ? currentRatio[1] : null,
    };

    return processedData;
  };

  const handleSubmit = async (selectedTransducer: RowDataTransducer | undefined, data: any) => {
    const processedData = processData(data);

    const payload = newTransducerModelType
      ? { externalDto: { tsoId: 1, ...processedData } }
      : { externalDto: { ...selectedTransducer, ...processedData } };

    try {
      if (newTransducerModelType) {
        await addEntity(payload).unwrap();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.transducer.new.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      } else {
        await editEntity(payload).unwrap();

        dispatch(
          setSnackbar({
            open: true,
            type: 'success',
            textContent: t('snackbar.transducer.update.success'),
            copyToClipboard: false,
            requestID: undefined,
          })
        );
      }
    } catch (err: any) {
      const requestID = err?.data?.traceId || undefined;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: newTransducerModelType
            ? t('snackbar.transducer.new.error')
            : t('snackbar.transducer.update.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      handleDiscard();
    }
  };

  const validationSchema = object({
    manufacturer: mandatoryStringSchema,
    model: mandatoryStringSchema,
    type: mandatory.and(baseOption),
    transformerRatioVoltage:
      currentType == '0' || currentType == '2' ? transformerRatioSchema.optional() : z.string().optional().nullable(),
    transformerRatioCurrent:
      currentType == '1' || currentType == '2' ? transformerRatioSchema.optional() : z.string().optional().nullable(),
    voltageFrequencyDeviationMagnitude1: stringPositiveIntegerSchema.optional(),
    voltageFrequencyDeviationMagnitude10: stringPositiveIntegerSchema.optional(),
    voltageFrequencyDeviationPhaseAngle1: stringPositiveIntegerSchema.optional(),
    voltageFrequencyDeviationPhaseAngle10: stringPositiveIntegerSchema.optional(),
    currentFrequencyDeviationMagnitude1: stringPositiveIntegerSchema.optional(),
    currentFrequencyDeviationMagnitude10: stringPositiveIntegerSchema.optional(),
    currentFrequencyDeviationPhaseAngle1: stringPositiveIntegerSchema.optional(),
    currentFrequencyDeviationPhaseAngle10: stringPositiveIntegerSchema.optional(),
    comment: commentSchema,
  });

  const renderFormContent = useCallback(
    (props: any) => (
      <FormContent
        {...props}
        updateDisabled={updateDisabled}
        defaultValues={defaultValues}
        onTypeChange={handleTypeChange}
      />
    ),
    [updateDisabled, defaultValues]
  );

  const handleDeleteConfirm = async () => {
    if (!selectedTransducer?.id) return;

    try {
      await deleteTransducerModelById(selectedTransducer.id).unwrap();

      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: t('snackbar.transducer.delete.success'),
          requestID: undefined,
        })
      );
    } catch (err: any) {
      const requestID = err?.data?.traceId;

      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: t('snackbar.transducer.delete.error'),
          copyToClipboard: true,
          requestID,
        })
      );
    } finally {
      handleDiscard();
    }
  };

  return (
    <FormModalWrapper
      show={!!(selectedTransducer || newTransducerModelType)}
      newItem={newTransducerModelType}
      title={t('masterdata.transducer.modal.title')}
      titleEdit={t('masterdata.transducer.modal.titleEdit')}
      onSubmit={(data: any) => handleSubmit(selectedTransducer, data)}
      onDiscard={handleDiscard}
      defaultValues={defaultValues}
      showDeleteButton={!!selectedTransducer}
      formContent={renderFormContent}
      formComponent={FormTsoPq}
      formProps={{ customProps: history, schema: validationSchema }}
      customWidth={customMaxWidth}
      deleteButtonDisabled={deleteDisabled}
      tooltipText={
        deleteDisabled
          ? t('management.measurementPoint.transducerModelCannotDelete')
          : t('management.measurementPoint.transducerModelDeleteTitle')
      }
      deleteTitle={t('management.measurementPoint.transducerModelDeleteTitle')}
      deleteText={t('management.measurementPoint.transducerModelDeleteContent')}
      onDeleteConfirm={handleDeleteConfirm}
    />
  );
};

export default TransducerModal;
